import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getArtistPrice } from "../api";
import { TradeTransaction } from "../api/sharedTypes";
import { currencyRounder } from "../helpers";
import { artistKeys } from "./artists";
import {
  useGetCurrentLineup,
  useGetNextWeekLineup,
  useTradeLineup,
} from "./lineups";

export enum RecruitError {
  NoData,
  InsufficientMoney,
  InLineup,
  ApiError,
}

export enum BoostError {
  NoData,
  NotInLineup,
  ApiError,
}

export enum DropError {
  NoData,
  NotInLineup,
  ApiError,
}

export enum RemoveBoostError {
  NoData,
  NotInLineup,
  ApiError,
}

interface LineupCacheState {
  lineup: string[];
  boostedArtist: string | undefined;
  money: number;
  trades: number;
}

export function useLineupCache(
  playerId: string,
  params: { draftId?: string; lineupIndex?: number },
) {
  const queryClient = useQueryClient();
  const tradeLineupMutation = useTradeLineup();

  const { data: nextWeekLineup } = useGetNextWeekLineup(playerId, params);
  const { data: currentLineup } = useGetCurrentLineup(playerId, params);

  const [state, setState] = useState<LineupCacheState | undefined>();
  const [originalState, setOriginalState] = useState<
    LineupCacheState | undefined
  >();
  const transactionsRef = useRef<TradeTransaction[]>([]);
  const [pendingChanges, setPendingChanges] = useState(false);

  useEffect(() => {
    if (!nextWeekLineup || !currentLineup) return;

    const newState: LineupCacheState = {
      lineup: nextWeekLineup.artists.map((a) => a.id),
      boostedArtist: nextWeekLineup.boostedArtistId ?? undefined,
      money: currentLineup.budget,
      trades: currentLineup.freeTrades,
    };
    setOriginalState({ ...newState });
    if (!pendingChanges) {
      setState({ ...newState });
    }
  }, [nextWeekLineup, currentLineup, pendingChanges]);

  const recruitArtist = async (
    artistId: string,
  ): Promise<{ success: boolean; error?: RecruitError }> => {
    if (!state || !params.draftId) {
      return { success: false, error: RecruitError.NoData };
    }
    if (state.lineup.includes(artistId)) {
      return { success: false, error: RecruitError.InLineup };
    }

    let artistPrice: number;
    try {
      const price = await queryClient.fetchQuery({
        queryKey: artistKeys.price(artistId, params.draftId),
        queryFn: () => getArtistPrice(artistId, params.draftId!),
        staleTime: 60 * 60 * 1000,
      });
      artistPrice = price.currentPrice;
    } catch {
      return { success: false, error: RecruitError.ApiError };
    }

    if (artistPrice > state.money) {
      return { success: false, error: RecruitError.InsufficientMoney };
    }

    setState({
      ...state,
      lineup: [...state.lineup, artistId],
      money: state.money - artistPrice,
    });
    transactionsRef.current.push({ type: "buy", artistId });
    setPendingChanges(true);

    return { success: true };
  };

  const dropArtist = async (
    artistId: string,
  ): Promise<{ success: boolean; error?: DropError }> => {
    if (!state || !params.draftId) {
      return { success: false, error: DropError.NoData };
    }
    if (!state.lineup.includes(artistId)) {
      return { success: false, error: DropError.NotInLineup };
    }

    let artistPrice: number;
    try {
      const price = await queryClient.fetchQuery({
        queryKey: artistKeys.price(artistId, params.draftId),
        queryFn: () => getArtistPrice(artistId, params.draftId!),
        staleTime: 60 * 60 * 1000,
      });
      artistPrice = price.currentPrice;
    } catch {
      return { success: false, error: DropError.ApiError };
    }

    // With a free trade: full refund + use one trade. Without: 50% refund.
    const refund =
      state.trades > 0 ? artistPrice : currencyRounder(artistPrice / 2);
    const trades = state.trades > 0 ? state.trades - 1 : 0;

    setState({
      ...state,
      lineup: state.lineup.filter((id) => id !== artistId),
      boostedArtist:
        state.boostedArtist === artistId ? undefined : state.boostedArtist,
      money: state.money + refund,
      trades,
    });
    transactionsRef.current.push({ type: "sell", artistId });
    setPendingChanges(true);

    return { success: true };
  };

  const boostArtist = (
    artistId: string,
  ): { success: boolean; error?: BoostError } => {
    if (!state) return { success: false, error: BoostError.NoData };
    if (!state.lineup.includes(artistId)) {
      return { success: false, error: BoostError.NotInLineup };
    }

    setState({ ...state, boostedArtist: artistId });
    transactionsRef.current.push({ type: "set_boost", artistId });
    setPendingChanges(true);

    return { success: true };
  };

  const removeBoost = (): { success: boolean; error?: RemoveBoostError } => {
    if (!state) return { success: false, error: RemoveBoostError.NoData };
    if (!state.boostedArtist) {
      return { success: false, error: RemoveBoostError.NotInLineup };
    }

    setState({ ...state, boostedArtist: undefined });
    transactionsRef.current.push({ type: "remove_boost" });
    setPendingChanges(true);

    return { success: true };
  };

  const resetCache = () => {
    if (!originalState) return;
    setState({ ...originalState });
    transactionsRef.current = [];
    setPendingChanges(false);
  };

  const sync = (options?: { onSuccess?: () => void }) => {
    if (
      !pendingChanges ||
      !state ||
      !params.draftId ||
      params.lineupIndex === undefined
    )
      return;

    tradeLineupMutation.mutate(
      {
        draftId: params.draftId,
        lineupIndex: params.lineupIndex,
        transactions: transactionsRef.current,
        expectedOutcome: {
          artistIds: state.lineup,
          boostedArtistId: state.boostedArtist ?? null,
        },
      },
      {
        onSuccess: () => {
          setPendingChanges(false);
          transactionsRef.current = [];
          options?.onSuccess?.();
        },
      },
    );
  };

  return {
    state,
    pendingChanges,
    isSyncing: tradeLineupMutation.isPending,
    syncError: tradeLineupMutation.error,
    recruitArtist,
    boostArtist,
    dropArtist,
    removeBoost,
    resetCache,
    sync,
  };
}
